import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Boxes, ChevronRight } from 'lucide-react';
import type { InfraIndex, InfraItem } from '@simpleacs/shared';
import { settingsTabPath } from '@simpleacs/shared';
import { api } from '../../lib/api';
import {
  Card,
  EmptyState,
  ErrorNotice,
  PageHeader,
  Pill,
  Spinner,
  StatCard,
} from '../../components/ui';

/**
 * The Infrastructure index — the section's landing page, and the first entry in
 * its nav. One row per thing the section covers with its live state, answering
 * "is everything alright?" in one screen; each row opens the one page where
 * that thing is watched and managed. The network, the instance-wide machinery
 * and the per-organization settings stay separate groups, and this page
 * deliberately holds no controls of its own — an INDEX, not a stack of panels.
 */
const DETAIL_ROUTE: Record<InfraItem['id'], string> = {
  genieacs: '/infrastructure/genieacs',
  wireguard: '/infrastructure/tunnels',
  openvpn: '/infrastructure/tunnels',
  tr069: '/settings/tr069-profiles',
  deployment: '/infrastructure/deploy',
  backups: '/infrastructure/backups',
  // The section's other nav entries, each opening its own page.
  olts: '/olts',
  gateways: '/gateways',
  topology: '/topology',
  // Platform plumbing that lives on the superadmin's own settings cards. Each
  // names its Settings TAB rather than the page: `/settings` alone dropped the
  // operator on Overview to hunt for the card the row they clicked was about.
  // `settingsTabPath` builds the link from the same tab vocabulary the page
  // resolves against, so a renamed tab breaks the build instead of the link.
  storage: settingsTabPath('storage'),
  map: settingsTabPath('map'),
  // The Deployment page is this running install; the scheduler is that
  // install's background timers, so it is where the detail belongs.
  scheduler: '/infrastructure/deploy',
};

/**
 * `off` is its own tone and its own word.
 *
 * "not configured" is a statement about intent — an optional integration nobody
 * has switched on, or a list nobody has added to. "unknown" is a statement
 * about this page — the reading could not be taken. Painting either one amber
 * would turn a working platform into a page of false alarms.
 */
const STATE_TONE = {
  ok: 'success',
  warn: 'warn',
  down: 'danger',
  unknown: 'neutral',
  off: 'info',
} as const;
const STATE_LABEL = {
  ok: 'healthy',
  warn: 'attention',
  down: 'down',
  unknown: 'unknown',
  off: 'not configured',
} as const;

export function PlatformIndexPage() {
  const query = useQuery({
    queryKey: ['infra', 'index'],
    queryFn: () => api.get<InfraIndex>('/infra'),
    refetchInterval: 30_000,
  });

  const items = query.data?.items ?? [];
  const counts = {
    ok: items.filter((i) => i.state === 'ok').length,
    warn: items.filter((i) => i.state === 'warn').length,
    down: items.filter((i) => i.state === 'down').length,
  };

  return (
    <>
      <PageHeader
        title="Platform"
        description="Everything under Infrastructure — the network SimpleACS runs, and the platform it runs on. Each row opens where it is watched and managed."
      />
      {query.isLoading ? (
        <div className="flex justify-center p-16">
          <Spinner className="size-6" />
        </div>
      ) : query.error ? (
        <ErrorNotice error={query.error} />
      ) : (
        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard label="Components" value={items.length} icon={<Boxes className="size-4" />} />
            {/*
              Green when nothing is unhappy, not when everything is `ok`.
              A row that is honestly "not configured" — no Google key, no
              SmartOLT connection — must not hold this card grey for ever on a
              platform where nothing is actually wrong.
            */}
            <StatCard
              label="Healthy"
              value={counts.ok}
              tone={counts.ok > 0 && counts.warn === 0 && counts.down === 0 ? 'success' : 'default'}
            />
            <StatCard label="Need attention" value={counts.warn} tone={counts.warn > 0 ? 'warn' : 'default'} />
            <StatCard label="Down" value={counts.down} tone={counts.down > 0 ? 'danger' : 'success'} />
          </div>

          <IndexGroup
            title="Network"
            description="The fleet this section manages — each row opens its own page."
            items={items.filter((i) => i.scope === 'network')}
          />
          <IndexGroup
            title="Instance-wide"
            description="One of each per deployment, shared by every organization."
            items={items.filter((i) => i.scope === 'instance')}
          />
          <IndexGroup
            title="Per-organization"
            description="Owned per tenant; opens the pages where each organization's own are managed."
            items={items.filter((i) => i.scope === 'org')}
          />
        </div>
      )}
    </>
  );
}

function IndexGroup({
  title,
  description,
  items,
}: {
  title: string;
  description: string;
  items: InfraItem[];
}) {
  if (items.length === 0)
    return (
      <Card title={title}>
        <EmptyState title="Nothing here" description={description} />
      </Card>
    );
  return (
    <Card title={title}>
      <p className="border-b border-line px-5 py-2.5 text-xs text-faint">{description}</p>
      <div className="divide-y divide-line">
        {items.map((item) => (
          <Link
            key={item.id}
            to={DETAIL_ROUTE[item.id]}
            className="flex items-center gap-4 px-5 py-3.5 transition-colors hover:bg-surface-2"
          >
            <Pill tone={STATE_TONE[item.state]} dot>
              {STATE_LABEL[item.state]}
            </Pill>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-ink">{item.name}</span>
              <span className="block truncate text-xs text-muted">{item.description}</span>
            </span>
            <span className="hidden max-w-[22rem] truncate text-xs text-faint sm:block" title={item.status}>
              {item.status}
            </span>
            <ChevronRight className="size-4 shrink-0 text-faint" />
          </Link>
        ))}
      </div>
    </Card>
  );
}
